import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV1MissionsMissionIdControlQueryKey,
  useGetApiV1MissionsMissionIdControl,
  usePostApiV1MissionsMissionIdControl,
  usePostApiV1MissionsMissionIdControlAnswer,
  usePostApiV1MissionsMissionIdControlHandover,
} from '@/shared/api/generated/missions/missions';
import type { ControlCandidateDto, MissionControlDto } from '@/shared/api/generated/model';
import { problemDetail } from '@/shared/api/problem';
import { SystemRoles, useCurrentUserId, useRoles } from '@/features/auth/model/use-roles';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { formatDateTime } from '@/shared/lib/format/dates';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { Panel } from '@/shared/ui/Panel';
import { QueryError } from '@/shared/ui/QueryError';
import { LiveEvery, liveEvery } from '@/shared/lib/live';

/**
 * Who is watching this mission (§23.1).
 *
 * ⚠️ **One control per mission** — instructed 22 August 2026. This does not reinstate the cap of
 * three withdrawn on 21 August: that was about how many control members may *follow* a mission,
 * which is still unlimited. This is about who is *answerable* for it, and it moves by handover.
 *
 * ⚠️ The panel offers three different things to three different readers and the conditions are kept
 * apart rather than collapsed: a desk operator answers the offer, the Head of Control assigns, and
 * the operator on watch hands over. Whether the API allows any of them is the API's decision (§12.2);
 * these gates only avoid drawing a button whose request is certain to fail.
 */
export function MissionControl({ missionId }: { missionId: string }) {
  const { t } = useLocale();
  const query = useGetApiV1MissionsMissionIdControl(missionId, liveEvery(LiveEvery.work));

  if (query.isPending) {
    return <Panel title={t.missions.controlHeading}>{null}</Panel>;
  }

  if (query.isError) {
    return (
      <Panel title={t.missions.controlHeading}>
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      </Panel>
    );
  }

  return <Loaded missionId={missionId} control={query.data} />;
}

function Loaded({ missionId, control }: { missionId: string; control: MissionControlDto }) {
  const { t, locale } = useLocale();
  const { hasAny } = useRoles();
  const me = useCurrentUserId();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [handingOver, setHandingOver] = useState(false);

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({
      queryKey: getGetApiV1MissionsMissionIdControlQueryKey(missionId),
    });
  };

  const onError = (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed);

  const answer = usePostApiV1MissionsMissionIdControlAnswer({
    mutation: { onSuccess: refresh, onError },
  });
  const assign = usePostApiV1MissionsMissionIdControl({
    mutation: { onSuccess: refresh, onError },
  });
  const handover = usePostApiV1MissionsMissionIdControlHandover({
    mutation: {
      onSuccess: async () => {
        setHandingOver(false);
        await refresh();
      },
      onError,
    },
  });

  const isControl = hasAny(
    SystemRoles.DeskControl,
    SystemRoles.HeadOfControl,
    SystemRoles.SystemAdministrator,
  );
  const isHead = hasAny(SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  const holder = control.holder ?? null;
  const iHoldIt = holder != null && holder.userId === me;
  const myAnswer = control.candidates.find((candidate) => candidate.userId === me) ?? null;

  const accepted = control.candidates.filter((candidate) => candidate.accepted);

  return (
    <Panel title={t.missions.controlHeading}>
      {/* ⚠️ The unattended state is said out loud, not left as an empty row. A mission nobody is
          watching is the thing an operator opening this screen most needs to notice. */}
      {holder ? (
        <p className="text-sm">
          <span className="text-muted-foreground">{t.missions.controlHolder}: </span>
          <span className="font-medium">{holder.fullName}</span>
          <span className="text-muted-foreground">
            {' · '}
            {t.missions.controlSince} {formatDateTime(holder.sinceUtc, locale)}
          </span>
          {iHoldIt ? <span className="text-primary"> — {t.missions.controlYouHold}</span> : null}
        </p>
      ) : (
        <p className="text-sm text-warning">{t.missions.controlUnattended}</p>
      )}

      {/* The question itself, to somebody who could answer it and has not — or has, and may change
          their mind, because a shift pattern is not a single decision. */}
      {isControl && !iHoldIt ? (
        <div className="flex flex-wrap items-center gap-3">
          {myAnswer === null ? (
            <>
              <p className="text-sm">{t.missions.controlOfferQuestion}</p>
              <Button
                size="sm"
                disabled={answer.isPending}
                onClick={() =>
                  answer.mutate({ missionId, data: { accepted: true } })
                }
              >
                {t.missions.controlAccept}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={answer.isPending}
                onClick={() =>
                  answer.mutate({ missionId, data: { accepted: false } })
                }
              >
                {t.missions.controlDecline}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {myAnswer.accepted ? t.missions.controlYouAccepted : t.missions.controlYouDeclined}
              </p>
              <Button
                size="sm"
                variant="ghost"
                disabled={answer.isPending}
                onClick={() =>
                  answer.mutate({ missionId, data: { accepted: !myAnswer.accepted } })
                }
              >
                {t.missions.controlChangeAnswer}
              </Button>
            </>
          )}
        </div>
      ) : null}

      {/* Who answered, and — for the Head of Control, with nobody on watch — the assignment. */}
      {isControl ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">{t.missions.controlCandidates}</h3>

          {control.candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.missions.controlNoCandidates}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {control.candidates.map((candidate) => (
                <CandidateRow
                  key={candidate.userId}
                  candidate={candidate}
                  canAssign={isHead && holder === null && candidate.accepted}
                  pending={assign.isPending}
                  onAssign={() =>
                    assign.mutate({ missionId, data: { userId: candidate.userId } })
                  }
                />
              ))}
            </ul>
          )}

          {isHead && holder === null ? (
            <p className="text-xs text-muted-foreground">{t.missions.controlAssignHint}</p>
          ) : null}
        </section>
      ) : null}

      {/* Only the operator actually on watch. Every desk operator passes the control policy, so a
          role check alone would offer this to everybody looking at the mission. */}
      {iHoldIt ? (
        handingOver ? (
          <HandoverForm
            candidates={accepted.filter((candidate) => candidate.userId !== me)}
            pending={handover.isPending}
            onCancel={() => setHandingOver(false)}
            onSubmit={(toUserId, notes) =>
              handover.mutate({ missionId, data: { toUserId, notes } })
            }
          />
        ) : (
          <div>
            <Button size="sm" variant="ghost" onClick={() => setHandingOver(true)}>
              {t.missions.controlHandover}
            </Button>
          </div>
        )
      ) : null}

      {control.history.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">{t.missions.controlHistory}</h3>

          <ul className="flex flex-col gap-1 text-sm">
            {control.history.map((shift) => (
              <li key={`${shift.userId}-${shift.startedAtUtc}`} className="flex flex-wrap gap-x-2">
                <span className="font-medium">{shift.fullName}</span>
                <span className="text-muted-foreground">
                  {formatDateTime(shift.startedAtUtc, locale)}
                  {' → '}
                  {shift.endedAtUtc
                    ? formatDateTime(shift.endedAtUtc, locale)
                    : t.missions.controlShiftOpen}
                </span>

                {/* ⚠️ A shift that ended with nobody taking it is named, not left blank. The gap is
                    the point of recording shifts at all. */}
                {shift.endedAtUtc && !shift.handedToUserId ? (
                  <span className="text-warning">{t.missions.controlEndedAlone}</span>
                ) : null}

                {shift.notes ? (
                  <span className="text-muted-foreground">— {shift.notes}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </Panel>
  );
}

function CandidateRow({
  candidate,
  canAssign,
  pending,
  onAssign,
}: {
  candidate: ControlCandidateDto;
  canAssign: boolean;
  pending: boolean;
  onAssign: () => void;
}) {
  const { t, locale } = useLocale();

  return (
    <li className="flex flex-wrap items-center gap-3 text-sm">
      <span className="font-medium">{candidate.fullName}</span>

      <Chip tone={candidate.accepted ? 'approved' : 'neutral'}>
        {candidate.accepted ? t.missions.controlAccepted : t.missions.controlDeclined}
      </Chip>

      <span className="text-xs text-muted-foreground">
        {formatDateTime(candidate.answeredAtUtc, locale)}
      </span>

      {canAssign ? (
        <Button size="sm" disabled={pending} onClick={onAssign}>
          {t.missions.controlAssign}
        </Button>
      ) : null}
    </li>
  );
}

/**
 * A shift change.
 *
 * ⚠️ Ending with no successor is offered rather than hidden. A control room does not always have
 * somebody to take over, and a mission that is visibly unattended can be alerted on — while one
 * attributed to an operator who has gone home cannot.
 */
function HandoverForm({
  candidates,
  pending,
  onCancel,
  onSubmit,
}: {
  candidates: readonly ControlCandidateDto[];
  pending: boolean;
  onCancel: () => void;
  onSubmit: (toUserId: string | null, notes: string | null) => void;
}) {
  const { t } = useLocale();
  const [toUserId, setToUserId] = useState('');
  const [notes, setNotes] = useState('');

  const nobody = toUserId === '';

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(nobody ? null : toUserId, notes.trim() === '' ? null : notes.trim());
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t.missions.controlHandoverTo}</span>
        <select
          className="h-11 rounded-lg border border-border bg-surface px-3"
          value={toUserId}
          onChange={(event) => setToUserId(event.target.value)}
        >
          <option value="">{t.missions.controlHandoverNobody}</option>
          {candidates.map((candidate) => (
            <option key={candidate.userId} value={candidate.userId}>
              {candidate.fullName}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t.missions.controlHandoverNotes}</span>
        <textarea
          className="min-h-20 rounded-lg border border-border bg-surface p-3"
          value={notes}
          placeholder={t.missions.controlHandoverNotesPlaceholder}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {nobody ? <p className="text-xs text-warning">{t.missions.controlHandoverWarning}</p> : null}

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={pending}>
          {t.missions.controlHandoverConfirm}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          {t.missions.cancel}
        </Button>
      </div>
    </form>
  );
}
